<template>
  <div class="site-shell">
    <header class="hero">
      <div class="hero-noise"></div>
      <nav class="topbar wrap">
        <button class="brand brand-button" type="button" @click="scrollToTop">
          <img class="brand-mark" :src="brandLogo" alt="数企推">
          <span class="brand-copy">
            <span class="brand-name">数企推</span>
            <span class="brand-sub">Smart Growth Portal</span>
          </span>
        </button>

        <div class="nav-group">
          <button
            v-for="item in navItems"
            :key="item.id"
            class="nav-link"
            type="button"
            @click="scrollToSection(item.id)"
          >
            {{ item.label }}
          </button>
          <a class="button button-ghost button-compact" href="https://card.utherdata.com/admin/login">登录后台</a>
        </div>
      </nav>

      <div class="hero-content wrap">
        <section class="hero-copy">
          <p class="hero-kicker">企业数字名片 + 官网展示 + 线索转化</p>
          <h1>让每一次企业触达，都变成可追踪的品牌曝光和增长线索</h1>
          <p class="hero-text">
            数企推把企业官网、员工数字名片、宣传物料、活动页和留资表单整合到同一套增长入口里，
            帮助销售、市场和渠道团队更快展示实力，更稳承接客户，更轻松沉淀数据。
          </p>

          <div class="hero-actions">
            <button class="button button-primary" type="button" @click="scrollToSection('contact')">预约演示</button>
            <button class="button button-secondary" type="button" @click="scrollToSection('features')">查看功能</button>
          </div>

          <ul class="hero-badges">
            <li v-for="badge in heroBadges" :key="badge">{{ badge }}</li>
          </ul>

          <div class="hero-metrics">
            <article v-for="metric in heroMetrics" :key="metric.label" class="metric-card">
              <strong>{{ metric.value }}</strong>
              <span>{{ metric.label }}</span>
              <p>{{ metric.desc }}</p>
            </article>
          </div>
        </section>

        <section class="hero-stage" aria-label="数企推产品展示">
          <div class="stage-orbit orbit-one"></div>
          <div class="stage-orbit orbit-two"></div>
          <div class="floating-chip chip-left">品牌内容库</div>
          <div class="floating-chip chip-right">线索自动沉淀</div>
          <div class="floating-chip chip-bottom">活动页一键分发</div>

          <div class="device device-left">
            <div class="device-notch"></div>
            <div class="device-screen">
              <div class="screen-header">
                <span>企业名片</span>
                <span class="screen-pill">认证</span>
              </div>
              <div class="screen-profile">
                <div class="avatar avatar-small"></div>
                <div>
                  <p>市场总监</p>
                  <span>品牌介绍 / 案例 / 联系方式</span>
                </div>
              </div>
              <div class="screen-banner screen-banner-soft"></div>
              <ul class="screen-list">
                <li></li>
                <li></li>
                <li></li>
              </ul>
            </div>
          </div>

          <div class="device device-center">
            <div class="device-notch"></div>
            <div class="device-screen">
              <div class="screen-header">
                <span>数企推品牌页</span>
                <span class="screen-pill screen-pill-accent">HOT</span>
              </div>
              <div class="screen-banner screen-banner-strong">
                <div>
                  <p>企业形象统一展示</p>
                  <span>官网内容、案例、视频、表单一页承接</span>
                </div>
              </div>
              <div class="screen-profile screen-profile-lg">
                <div class="avatar avatar-large"></div>
                <div>
                  <p>数字名片中心</p>
                  <span>批量开通员工名片，统一品牌输出</span>
                </div>
              </div>
              <div class="screen-stats">
                <div>
                  <strong>28%</strong>
                  <span>留资率</span>
                </div>
                <div>
                  <strong>4.6x</strong>
                  <span>内容触达</span>
                </div>
              </div>
              <ul class="screen-list screen-list-wide">
                <li></li>
                <li></li>
                <li></li>
                <li></li>
              </ul>
            </div>
          </div>

          <div class="device device-right">
            <div class="device-notch"></div>
            <div class="device-screen">
              <div class="screen-header">
                <span>数据看板</span>
                <span class="screen-pill">实时</span>
              </div>
              <div class="chart-bars">
                <span class="bar bar-short"></span>
                <span class="bar bar-mid"></span>
                <span class="bar bar-high"></span>
                <span class="bar bar-mid"></span>
              </div>
              <div class="screen-stats screen-stats-compact">
                <div>
                  <strong>836</strong>
                  <span>浏览</span>
                </div>
                <div>
                  <strong>124</strong>
                  <span>留资</span>
                </div>
              </div>
              <ul class="screen-list">
                <li></li>
                <li></li>
                <li></li>
              </ul>
            </div>
          </div>
        </section>
      </div>
    </header>

    <main>
      <section id="features" class="section section-feature">
        <div class="wrap">
          <div class="section-heading">
            <p class="section-kicker">核心产品能力</p>
            <h2>一套系统串起企业展示、员工传播、客户留资和后续跟进</h2>
            <p>
              页面风格参考数字名片类官网，但把“数企推”的价值表达得更完整:
              不只是一张名片，而是企业对外内容和获客动作的统一门户。
            </p>
          </div>

          <div class="feature-showcase">
            <div class="feature-menu">
              <div class="feature-menu-tip">
                <span class="feature-menu-dot"></span>
                <p>点击左侧能力卡片，切换右侧展示内容</p>
              </div>

              <button
                v-for="(feature, index) in featureTabs"
                :key="feature.title"
                class="feature-tab"
                :class="{ active: activeFeatureIndex === index }"
                type="button"
                :aria-pressed="activeFeatureIndex === index ? 'true' : 'false'"
                @click="selectFeature(index)"
              >
                <span class="feature-tab-tag">{{ feature.tag }}</span>
                <strong>{{ feature.title }}</strong>
                <p>{{ feature.brief }}</p>
              </button>
            </div>

            <div class="feature-preview">
              <div class="preview-topbar">
                <span class="dot"></span>
                <span class="dot"></span>
                <span class="dot"></span>
                <p>{{ activeFeature.tag }} Workspace</p>
              </div>

              <div class="preview-body">
                <div class="preview-copy">
                  <p class="preview-eyebrow">{{ activeFeature.title }}</p>
                  <h3>{{ activeFeature.headline }}</h3>
                  <p class="preview-text">{{ activeFeature.desc }}</p>
                  <ul class="preview-points">
                    <li v-for="point in activeFeature.points" :key="point">{{ point }}</li>
                  </ul>
                </div>

                <div class="preview-panel">
                  <div class="panel-glass">
                    <div class="panel-chip-row">
                      <span v-for="stat in activeFeature.stats" :key="stat" class="panel-chip">{{ stat }}</span>
                    </div>
                    <div class="panel-mock">
                      <div class="mock-sidebar">
                        <span></span>
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                      <div class="mock-main">
                        <div class="mock-hero"></div>
                        <div class="mock-grid">
                          <span></span>
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                        <div class="mock-footer"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="cases" class="section section-cases">
        <div class="wrap">
          <div class="section-heading section-heading-light">
            <p class="section-kicker">案例展示</p>
            <h2>数企推适合把“企业介绍”升级成“持续转化的业务入口”</h2>
            <p>
              参考站点中案例卡片的节奏，我们把它升级为更偏商务增长的表达，
              让来访客户能快速理解不同团队怎么用、用了之后会带来什么结果。
            </p>
          </div>

          <div class="case-grid">
            <article v-for="item in cases" :key="item.title" class="case-card">
              <div class="case-visual">
                <div class="case-phone">
                  <div class="case-phone-head"></div>
                  <div class="case-phone-body">
                    <div class="case-phone-banner"></div>
                    <div class="case-phone-profile"></div>
                    <div class="case-phone-list">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              </div>
              <p class="case-tag">{{ item.tag }}</p>
              <h3>{{ item.title }}</h3>
              <p class="case-desc">{{ item.desc }}</p>
              <div class="case-stats">
                <span v-for="stat in item.stats" :key="stat">{{ stat }}</span>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section id="scenarios" class="section section-growth">
        <div class="wrap growth-layout">
          <div class="growth-board">
            <div class="board-toolbar">
              <span>企业成员总览</span>
              <span class="board-pill">批量管理</span>
            </div>
            <div class="board-snapshot">
              <div class="board-sidebar">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <div class="board-main">
                <div class="member-row" v-for="member in memberRows" :key="member.name">
                  <div class="member-id">
                    <div class="avatar avatar-row"></div>
                    <div>
                      <strong>{{ member.name }}</strong>
                      <span>{{ member.role }}</span>
                    </div>
                  </div>
                  <span class="member-status">{{ member.status }}</span>
                  <span class="member-data">{{ member.metric }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="growth-copy">
            <p class="section-kicker">快速部署</p>
            <h2>快速为企业成员生成统一数字名片，团队对外形象不再散乱</h2>
            <p>
              从总部品牌模板到分公司、部门、个人名片统一发放，再到文章、海报、活动页同步更新，
              数企推让每个成员都拥有标准化却不僵硬的对外展示页。
            </p>

            <div class="growth-points">
              <article v-for="point in growthPoints" :key="point.title" class="growth-point">
                <strong>{{ point.title }}</strong>
                <p>{{ point.desc }}</p>
              </article>
            </div>
          </div>
        </div>
      </section>

      <section class="section section-solutions">
        <div class="wrap">
          <div class="section-heading">
            <p class="section-kicker">应用场景</p>
            <h2>不是单点展示页，而是一套适合市场、销售、服务与渠道团队的业务工具</h2>
          </div>

          <div class="solution-grid">
            <article v-for="scenario in solutions" :key="scenario.title" class="solution-card">
              <span class="solution-index">{{ scenario.index }}</span>
              <h3>{{ scenario.title }}</h3>
              <p>{{ scenario.desc }}</p>
              <ul>
                <li v-for="point in scenario.points" :key="point">{{ point }}</li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      <section id="delivery" class="section section-process">
        <div class="wrap">
          <div class="section-heading">
            <p class="section-kicker">增长路径</p>
            <h2>从品牌素材整理到线索承接上线，控制上线节奏，也控制转化质量</h2>
          </div>

          <div class="process-grid">
            <article v-for="step in processSteps" :key="step.title" class="process-card">
              <span class="process-step">{{ step.step }}</span>
              <h3>{{ step.title }}</h3>
              <p>{{ step.desc }}</p>
            </article>
          </div>
        </div>
      </section>

      <section class="section section-highlight">
        <div class="wrap highlight-card">
          <div class="highlight-copy">
            <p class="section-kicker">价值亮点</p>
            <h2>把“官网有人看”升级成“官网有人看、有人留、有人跟”</h2>
            <p>
              数企推把传统官网难以落地的转化动作前置到每一次分享和每一张员工名片里，
              既能服务品牌形象，也能直接服务业务成交。
            </p>
            <button class="button button-primary" type="button" @click="scrollToSection('contact')">获取方案报价</button>
          </div>

          <div class="highlight-metrics">
            <article v-for="item in highlightMetrics" :key="item.label" class="highlight-stat">
              <strong>{{ item.value }}</strong>
              <span>{{ item.label }}</span>
            </article>
          </div>
        </div>
      </section>

      <section id="contact" class="section section-contact">
        <div class="wrap">
          <div class="contact-card">
            <div class="contact-copy">
              <p class="section-kicker">立即咨询</p>
              <h2>为你的企业做一套更像“增长系统”的官网和数字名片方案</h2>
              <p>
                适合品牌升级、销售获客、会展扫码、渠道招商、顾问式服务展示等场景。
                如果你已经有企业官网，我们也可以把数企推作为对外传播和线索承接层接入现有体系。
              </p>
            </div>

            <div class="contact-panel">
              <div v-for="item in contactCards" :key="item.title" class="contact-item">
                <strong>{{ item.title }}</strong>
                <p>{{ item.desc }}</p>
              </div>

              <div class="contact-actions">
                <a class="button button-primary" href="mailto:hello@shuqitui.com">联系销售</a>
                <a class="button button-secondary" href="https://card.utherdata.com/admin/login">进入后台</a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>

    <footer class="footer wrap">
      <div>
        <p class="footer-brand">数企推</p>
        <p class="footer-text">企业数字名片与增长门户解决方案</p>
      </div>
      <p class="footer-text">© {{ currentYear }} Shuqitui. All rights reserved.</p>
    </footer>

    <div class="floating-tools">
      <transition name="tool-fade">
        <button
          v-if="showBackToTop"
          class="floating-button floating-button-top"
          type="button"
          aria-label="回到顶部"
          @click="scrollToTop"
        >
          <span class="floating-icon">↑</span>
          <span>顶部</span>
        </button>
      </transition>

      <div
        class="marketing-tool"
        @mouseenter="openMarketingPanel"
        @mouseleave="closeMarketingPanel"
      >
        <button
          class="floating-button floating-button-marketing"
          type="button"
          :aria-expanded="marketingPanelOpen ? 'true' : 'false'"
          @click="toggleMarketingPanel"
        >
          <span class="floating-icon floating-icon-logo">
            <img :src="brandLogo" alt="数企推">
          </span>
          <span>数企推</span>
        </button>

        <transition name="tool-fade">
          <div v-if="marketingPanelOpen" class="marketing-panel">
            <div class="marketing-panel-header">
              <strong>扫码获客</strong>
              <p>公众号与小程序入口</p>
            </div>

            <div class="marketing-qr-grid">
              <article class="qr-card">
                <div class="qr-code">
                  <img class="qr-image" :src="officialAccountQr" alt="数企推公众号二维码" loading="lazy">
                </div>
                <strong>公众号二维码</strong>
                <p>扫码关注，承接内容触达与品牌沉淀</p>
              </article>

              <article class="qr-card">
                <div class="qr-code">
                  <img class="qr-image" :src="miniProgramQr" alt="数企推小程序二维码" loading="lazy">
                </div>
                <strong>小程序二维码</strong>
                <p>扫码进入，体验数字名片与留资能力</p>
              </article>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'OfficialSite',
  data () {
    return {
      brandLogo: require('../../static/shuqitui-logo.png'),
      officialAccountQr: require('../../static/shuqitui-official-account-qr.jpg'),
      miniProgramQr: require('../../static/shuqitui-mini-program-qr.png'),
      activeFeatureIndex: 0,
      featureRotationTimer: null,
      isMobileView: false,
      marketingPanelOpen: false,
      showBackToTop: false,
      currentYear: new Date().getFullYear(),
      navItems: [
        { id: 'features', label: '核心功能' },
        { id: 'cases', label: '案例展示' },
        { id: 'scenarios', label: '应用场景' },
        { id: 'delivery', label: '增长路径' },
        { id: 'contact', label: '联系我们' }
      ],
      heroBadges: [
        '企业官网式数字名片',
        '员工名片批量开通',
        '物料内容统一分发',
        '访问轨迹自动沉淀'
      ],
      heroMetrics: [
        {
          value: '1000+',
          label: '成员名片可快速启用',
          desc: '组织架构、职位、联系方式可统一管理'
        },
        {
          value: '24h',
          label: '活动页与专题页上线',
          desc: '无需重复找技术排期，市场动作更敏捷'
        },
        {
          value: '3x',
          label: '企业内容触达效率提升',
          desc: '员工分享、客户访问、留资转化统一闭环'
        }
      ],
      featureTabs: [
        {
          tag: 'Brand Hub',
          title: '企业品牌主页',
          brief: '把官网信息、案例、视频和联系方式收束成一个高完成度入口。',
          headline: '让客户先看到你是谁、能做什么、为什么值得信任',
          desc: '首页、产品页、案例页、联系方式和预约入口统一成品牌内容中心，既适合对外展示，也适合销售在聊天、邮件和会展现场快速发送。',
          points: [
            '企业介绍、产品能力、成功案例模块化搭建',
            '品牌色、封面图、视频、海报统一输出',
            '移动端优先，适合微信、企微和手机浏览场景'
          ],
          stats: ['品牌官网', '视频展示', '预约咨询']
        },
        {
          tag: 'Digital Card',
          title: '员工数字名片',
          brief: '统一企业成员对外身份，保证每次触达都专业、标准、可管理。',
          headline: '从总部模板到个人名片，一次配置，批量发放',
          desc: '支持按组织架构、部门、岗位快速生成数字名片，成员可以在统一品牌框架内展示个人介绍、服务方向和联系方式。',
          points: [
            '批量开通成员名片并统一品牌视觉',
            '支持个人二维码、电话、微信与导航入口',
            '员工离职、调岗、信息变更可后台集中维护'
          ],
          stats: ['批量开通', '二维码', '统一品牌']
        },
        {
          tag: 'Growth Engine',
          title: '营销裂变分发',
          brief: '文章、海报、活动页、表单和转介绍动作可以一页打通。',
          headline: '让每张名片都成为市场活动和销售转化的分发站',
          desc: '企业可以按活动、招商、直播、课程、产品发布等主题快速生成专题页，并通过员工名片和渠道伙伴进行传播，让内容分发更可控。',
          points: [
            '活动海报、文章、直播预约一键挂载',
            '表单留资、手机号收集、咨询跳转灵活配置',
            '传播动作可回溯到具体成员和具体内容'
          ],
          stats: ['活动页', '表单留资', '裂变分享']
        },
        {
          tag: 'Data Insight',
          title: '访问与线索看板',
          brief: '谁浏览了、看了什么、从哪里来，清楚沉淀到后台。',
          headline: '把“有人访问”进一步变成“知道谁有兴趣、谁该跟进”',
          desc: '数企推内置访问数据和留资统计，帮助企业识别高意向页面、热门内容和高转化成员，为后续跟进和内容优化提供依据。',
          points: [
            '浏览、分享、留资、咨询动作自动记录',
            '支持成员、部门、活动维度的数据汇总',
            '适合对接 CRM 或作为轻量线索池先行使用'
          ],
          stats: ['访问轨迹', '转化统计', '跟进依据']
        }
      ],
      cases: [
        {
          tag: '制造企业',
          title: '把工厂实力、产品方案和资质证书装进销售名片',
          desc: '适合需要频繁发送产品册、案例图片、工厂视频和联系方式的企业，让客户在手机端就能快速完成第一轮信任建立。',
          stats: ['工厂实力展示', '产品资料分发', '会展扫码获客']
        },
        {
          tag: '企业服务',
          title: '把顾问型团队的专业表达做得统一、清晰、易分享',
          desc: '咨询、软件、培训和服务型团队可以把顾问介绍、服务清单、案例结果和预约动作整合在个人名片里，提高沟通效率。',
          stats: ['顾问介绍', '服务套餐', '预约咨询']
        },
        {
          tag: '渠道招商',
          title: '把招商海报、政策说明和报名入口做成可转发专题页',
          desc: '适合加盟、渠道合作、区域招募场景，销售和渠道经理可以围绕单一活动快速生成传播页面并追踪线索来源。',
          stats: ['活动专题', '报名收集', '来源追踪']
        }
      ],
      memberRows: [
        { name: '李晨', role: '华东区客户经理', status: '已开通', metric: '本周访问 126' },
        { name: '周岚', role: '品牌运营负责人', status: '已开通', metric: '内容分发 42' },
        { name: '王昊', role: '招商主管', status: '已开通', metric: '线索留资 18' },
        { name: '陈予', role: '售前顾问', status: '审核中', metric: '页面浏览 76' }
      ],
      growthPoints: [
        {
          title: '组织化管理',
          desc: '部门、岗位、手机号、名片状态一屏可见，适合企业快速扩张时统一管理。'
        },
        {
          title: '内容同步更新',
          desc: '总部更新企业介绍、案例或活动物料后，成员展示页可同步使用最新内容。'
        },
        {
          title: '业务动作闭环',
          desc: '分享出去的不只是个人名片，而是带着品牌、内容、表单和跟进入口的业务页面。'
        }
      ],
      solutions: [
        {
          index: '01',
          title: '企业官网升级',
          desc: '适合希望快速拥有手机官网型落地页的企业，让介绍更轻、更快、更适合转发。',
          points: ['移动端优先设计', '官网内容模块化搭建', '表单与咨询入口前置']
        },
        {
          index: '02',
          title: '销售拜访获客',
          desc: '销售在微信、短信、邮件、展会扫码等场景下，可以更高效地发送企业介绍和个人名片。',
          points: ['二维码与链接分享', '客户访问轨迹沉淀', '名片和资料一体分发']
        },
        {
          index: '03',
          title: '市场活动推广',
          desc: '活动海报、直播预约、展会专题、招商报名都可以快速生成页面，避免重复开发。',
          points: ['活动专题页', '传播素材统一管理', '转化数据可追踪']
        },
        {
          index: '04',
          title: '顾问式服务展示',
          desc: '服务型团队可以把个人擅长领域、客户案例和预约方式整合在一张专业名片里。',
          points: ['顾问资料展示', '案例与评价呈现', '咨询动作即时承接']
        }
      ],
      processSteps: [
        {
          step: '01',
          title: '梳理品牌素材',
          desc: '整理企业介绍、产品能力、案例、视频、联系方式和活动物料，确定首页表达重点。'
        },
        {
          step: '02',
          title: '搭建官网与名片模板',
          desc: '完成品牌页视觉、员工名片模板、表单入口和常用传播页面的统一配置。'
        },
        {
          step: '03',
          title: '批量开通成员使用',
          desc: '按部门或岗位快速开通成员账号，让市场、销售、顾问和渠道团队都能开始使用。'
        },
        {
          step: '04',
          title: '追踪数据并持续优化',
          desc: '根据浏览、留资和传播数据优化首页内容、活动入口和高转化模块。'
        }
      ],
      highlightMetrics: [
        { value: '品牌统一', label: '每个成员都输出同一套企业形象' },
        { value: '获客提效', label: '内容展示和留资转化在同一路径完成' },
        { value: '数据沉淀', label: '每次访问都有来源、有行为、有依据' }
      ],
      contactCards: [
        {
          title: '适合哪些企业',
          desc: '制造业、ToB 服务、招商加盟、软件公司、顾问团队以及重视品牌与销售协同的组织。'
        },
        {
          title: '我们可以交付什么',
          desc: '官网首页、数字名片、专题活动页、内容库搭建、留资表单与后台管理配置。'
        },
        {
          title: '如何开始',
          desc: '先做演示与需求梳理，再根据你现有官网和品牌素材决定是整体搭建还是接入式升级。'
        }
      ]
    }
  },
  computed: {
    activeFeature () {
      return this.featureTabs[this.activeFeatureIndex]
    }
  },
  mounted () {
    this.updateViewportState()
    this.startFeatureRotation()
    this.handleScroll()
    window.addEventListener('scroll', this.handleScroll)
    window.addEventListener('resize', this.updateViewportState)
  },
  beforeDestroy () {
    this.stopFeatureRotation()
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('resize', this.updateViewportState)
  },
  methods: {
    updateViewportState () {
      const nextIsMobile = window.innerWidth <= 860
      if (this.isMobileView !== nextIsMobile) {
        this.isMobileView = nextIsMobile
        if (this.isMobileView) {
          this.stopFeatureRotation()
        } else {
          this.startFeatureRotation()
        }
      }
    },
    startFeatureRotation () {
      this.stopFeatureRotation()
      if (this.isMobileView || this.featureTabs.length <= 1) {
        return
      }
      this.featureRotationTimer = setInterval(() => {
        this.activeFeatureIndex = (this.activeFeatureIndex + 1) % this.featureTabs.length
      }, 4200)
    },
    stopFeatureRotation () {
      if (this.featureRotationTimer) {
        clearInterval(this.featureRotationTimer)
        this.featureRotationTimer = null
      }
    },
    selectFeature (index) {
      this.activeFeatureIndex = index
      if (!this.isMobileView) {
        this.startFeatureRotation()
      }
    },
    handleScroll () {
      this.showBackToTop = window.pageYOffset > 280
    },
    openMarketingPanel () {
      this.marketingPanelOpen = true
    },
    closeMarketingPanel () {
      this.marketingPanelOpen = false
    },
    toggleMarketingPanel () {
      this.marketingPanelOpen = !this.marketingPanelOpen
    },
    scrollToTop () {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    scrollToSection (id) {
      const el = document.getElementById(id)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  }
}
</script>

<style scoped>
.site-shell {
  --brand-strong: #0f6fff;
  --brand-soft: #eaf6ff;
  --ink: #10214d;
  --muted: rgba(16, 33, 77, 0.68);
  --line: rgba(15, 111, 255, 0.12);
  box-sizing: border-box;
  overflow-x: hidden;
  color: var(--ink);
  background:
    radial-gradient(circle at top left, rgba(57, 211, 255, 0.2), transparent 28%),
    linear-gradient(180deg, #f4fbff 0%, #ffffff 32%, #f3f8ff 100%);
}

.site-shell *,
.site-shell *::before,
.site-shell *::after {
  box-sizing: border-box;
}

.wrap {
  width: min(1180px, calc(100% - 40px));
  margin: 0 auto;
}

.hero {
  position: relative;
  overflow: hidden;
  padding: 24px 0 80px;
  background:
    radial-gradient(circle at 14% 24%, rgba(57, 211, 255, 0.28), transparent 26%),
    radial-gradient(circle at 85% 18%, rgba(13, 92, 255, 0.28), transparent 24%),
    linear-gradient(135deg, #0d7df3 0%, #27b3ff 52%, #41d4ff 100%);
  color: #ffffff;
}

.hero::before,
.hero::after,
.hero-noise::before {
  content: '';
  position: absolute;
  pointer-events: none;
}

.hero::before {
  inset: auto auto 20px -140px;
  width: 420px;
  height: 420px;
  border-radius: 50%;
  background:
    repeating-radial-gradient(circle, rgba(255, 255, 255, 0.18) 0 2px, transparent 2px 16px);
  opacity: 0.38;
}

.hero::after {
  top: 140px;
  right: 54px;
  width: 160px;
  height: 160px;
  transform: rotate(24deg);
  border-radius: 32px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.14), rgba(255, 255, 255, 0.02));
  box-shadow: 0 28px 60px rgba(6, 60, 150, 0.18);
}

.hero-noise::before {
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.14) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px);
  background-size: 72px 72px;
  opacity: 0.14;
}

.topbar,
.hero-content,
.footer {
  position: relative;
  z-index: 1;
}

.topbar {
  position: fixed;
  top: 12px;
  left: 50%;
  z-index: 30;
  width: min(1180px, calc(100% - 40px));
  margin: 0;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 14px 18px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 22px;
  background: rgba(14, 118, 243, 0.24);
  backdrop-filter: blur(18px);
  box-shadow: 0 18px 34px rgba(7, 69, 170, 0.16);
}

.brand {
  display: inline-flex;
  align-items: center;
  gap: 14px;
}

.brand-button {
  padding: 0;
  color: inherit;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.brand-mark {
  display: block;
  flex: none;
  width: 48px;
  height: 48px;
  padding: 6px;
  border: 1px solid rgba(15, 111, 255, 0.12);
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(236, 245, 255, 0.9));
  object-fit: contain;
  box-shadow: 0 18px 34px rgba(7, 69, 170, 0.16);
}

.brand-copy {
  display: grid;
  gap: 2px;
  text-align: left;
}

.brand-name,
.brand-sub,
.hero-kicker,
.section-kicker,
.preview-eyebrow,
.case-tag,
.solution-index,
.process-step,
.footer-brand {
  margin: 0;
}

.brand-name {
  font-size: 20px;
  font-weight: 700;
}

.brand-sub {
  font-size: 12px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  opacity: 0.8;
}

.nav-group {
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.nav-link {
  padding: 0;
  color: rgba(255, 255, 255, 0.9);
  background: transparent;
  border: 0;
  cursor: pointer;
  font-size: 14px;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.nav-link:hover,
.nav-link:focus {
  opacity: 1;
  transform: translateY(-1px);
}

.hero-content {
  display: grid;
  grid-template-columns: minmax(0, 1.03fr) minmax(430px, 0.97fr);
  gap: 38px;
  align-items: center;
  padding-top: 118px;
}

.hero-kicker,
.section-kicker,
.preview-eyebrow,
.case-tag {
  font-size: 13px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  font-weight: 700;
}

.hero-kicker {
  color: rgba(255, 255, 255, 0.82);
}

.hero-copy h1,
.section-heading h2,
.preview-copy h3,
.growth-copy h2,
.highlight-copy h2,
.contact-copy h2 {
  margin: 0;
  font-family: var(--app-font-family);
  font-weight: 600;
  letter-spacing: 0.02em;
}

.hero-copy h1 {
  margin-top: 18px;
  max-width: 700px;
  font-size: clamp(30px, 3.4vw, 46px);
  letter-spacing: 0.01em;
  line-height: 1.18;
}

.hero-text,
.section-heading p,
.preview-text,
.case-desc,
.growth-copy p,
.solution-card p,
.process-card p,
.contact-copy p,
.contact-item p,
.footer-text {
  line-height: 1.8;
}

.hero-text {
  max-width: 620px;
  margin: 22px 0 0;
  font-size: 17px;
  color: rgba(255, 255, 255, 0.86);
}

.hero-actions,
.contact-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 28px;
}

.button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 148px;
  padding: 14px 24px;
  border-radius: 999px;
  border: 0;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease, background 0.22s ease;
}

.button:hover,
.button:focus {
  transform: translateY(-2px);
}

.button-primary {
  color: #0d56dd;
  background: linear-gradient(135deg, #ffffff 0%, #eff7ff 100%);
  box-shadow: 0 20px 34px rgba(7, 69, 170, 0.2);
}

.button-secondary {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.26);
}

.button-ghost {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.18);
}

.button-compact {
  min-width: 110px;
  padding: 11px 18px;
}

.hero-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 28px 0 0;
  padding: 0;
  list-style: none;
}

.hero-badges li {
  padding: 10px 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: rgba(255, 255, 255, 0.92);
  font-size: 14px;
}

.hero-metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-top: 34px;
}

.metric-card {
  padding: 20px 18px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.16);
  backdrop-filter: blur(18px);
}

.metric-card strong,
.screen-stats strong,
.highlight-stat strong {
  display: block;
  font-family: var(--app-font-family);
  font-size: 30px;
  line-height: 1;
  color: #ffffff;
}

.metric-card span,
.metric-card p,
.screen-header,
.screen-profile span,
.screen-banner span,
.screen-stats span,
.panel-chip,
.member-id span,
.member-status,
.member-data,
.highlight-stat span {
  display: block;
}

.metric-card span {
  margin-top: 10px;
  font-size: 14px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.96);
}

.metric-card p {
  margin: 8px 0 0;
  font-size: 13px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.78);
}

.hero-stage {
  position: relative;
  min-height: 600px;
}

.stage-orbit {
  position: absolute;
  border-radius: 50%;
  border: 1px dashed rgba(255, 255, 255, 0.22);
}

.orbit-one {
  inset: 18px 30px auto auto;
  width: 420px;
  height: 420px;
}

.orbit-two {
  inset: 102px auto auto -10px;
  width: 260px;
  height: 260px;
}

.floating-chip {
  position: absolute;
  padding: 12px 18px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 18px 34px rgba(9, 49, 120, 0.14);
  backdrop-filter: blur(16px);
  font-size: 13px;
  font-weight: 700;
}

.chip-left {
  left: 18px;
  top: 88px;
  animation: float-chip 4.8s ease-in-out infinite;
}

.chip-right {
  right: 10px;
  top: 112px;
  animation: float-chip 5.6s ease-in-out infinite 0.7s;
}

.chip-bottom {
  left: 84px;
  bottom: 56px;
  animation: float-chip 5.2s ease-in-out infinite 0.3s;
}

.device {
  position: absolute;
  border-radius: 38px;
  padding: 14px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.94), rgba(234, 247, 255, 0.94));
  box-shadow:
    0 30px 60px rgba(7, 69, 170, 0.26),
    inset 0 1px 0 rgba(255, 255, 255, 0.74);
}

.device-left {
  left: 30px;
  bottom: 42px;
  width: 184px;
  transform: rotate(-5deg);
}

.device-center {
  left: 170px;
  top: 14px;
  width: 250px;
  z-index: 2;
}

.device-right {
  right: 8px;
  bottom: 84px;
  width: 180px;
  transform: rotate(6deg);
}

.device-notch {
  width: 38%;
  height: 12px;
  margin: 0 auto 12px;
  border-radius: 999px;
  background: #d7e9ff;
}

.device-screen {
  padding: 16px;
  border-radius: 28px;
  background: linear-gradient(180deg, #ffffff 0%, #f4fbff 100%);
  color: var(--ink);
}

.screen-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-size: 12px;
  font-weight: 700;
}

.screen-pill,
.board-pill {
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba(15, 111, 255, 0.12);
  color: var(--brand-strong);
  font-size: 11px;
}

.screen-pill-accent {
  background: rgba(57, 211, 255, 0.16);
  color: #0a7bc7;
}

.screen-profile {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 12px;
  align-items: center;
  margin-top: 14px;
}

.screen-profile-lg {
  margin-top: 18px;
}

.screen-profile p,
.screen-banner p,
.preview-copy h3,
.solution-card h3,
.process-card h3,
.contact-item strong,
.member-id strong {
  margin: 0;
}

.screen-profile p,
.screen-banner p {
  font-size: 13px;
  font-weight: 700;
}

.screen-profile span,
.screen-banner span {
  margin-top: 5px;
  color: rgba(16, 33, 77, 0.58);
  font-size: 11px;
  line-height: 1.5;
}

.avatar {
  border-radius: 50%;
  background: linear-gradient(135deg, #9bc8ff, #2ab5ff);
}

.avatar-small {
  width: 42px;
  height: 42px;
}

.avatar-large {
  width: 54px;
  height: 54px;
}

.avatar-row {
  width: 34px;
  height: 34px;
}

.screen-banner {
  margin-top: 16px;
  border-radius: 22px;
}

.screen-banner-soft {
  height: 72px;
  background: linear-gradient(135deg, #e8f4ff, #eef9ff);
}

.screen-banner-strong {
  display: flex;
  align-items: flex-end;
  min-height: 110px;
  padding: 18px;
  background: linear-gradient(135deg, #0e74ff, #2bd1ff);
  color: #ffffff;
}

.screen-banner-strong span {
  color: rgba(255, 255, 255, 0.82);
}

.screen-stats {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 18px;
}

.screen-stats > div {
  padding: 14px;
  border-radius: 20px;
  background: #eef7ff;
}

.screen-stats strong {
  color: var(--brand-strong);
  font-size: 24px;
}

.screen-stats span {
  margin-top: 6px;
  color: rgba(16, 33, 77, 0.62);
  font-size: 11px;
}

.screen-stats-compact strong {
  font-size: 20px;
}

.screen-list {
  display: grid;
  gap: 9px;
  margin: 16px 0 0;
  padding: 0;
  list-style: none;
}

.screen-list li,
.mock-sidebar span,
.mock-grid span,
.member-status,
.member-data {
  border-radius: 999px;
}

.screen-list li {
  height: 11px;
  background: #dfeeff;
}

.screen-list-wide li:nth-child(1) {
  width: 92%;
}

.screen-list-wide li:nth-child(2) {
  width: 68%;
}

.screen-list-wide li:nth-child(3) {
  width: 84%;
}

.screen-list-wide li:nth-child(4) {
  width: 58%;
}

.chart-bars {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 9px;
  align-items: end;
  height: 106px;
  margin-top: 18px;
}

.bar {
  border-radius: 18px 18px 10px 10px;
  background: linear-gradient(180deg, #24d0ff, #0e6fff);
}

.bar-short {
  height: 36%;
}

.bar-mid {
  height: 62%;
}

.bar-high {
  height: 92%;
}

.section {
  padding: 96px 0;
}

.section-heading {
  max-width: 680px;
}

.section-heading h2,
.growth-copy h2,
.highlight-copy h2,
.contact-copy h2 {
  font-size: clamp(24px, 2.4vw, 36px);
  line-height: 1.32;
}

.section-heading p,
.growth-copy p,
.contact-copy p {
  margin: 16px 0 0;
  color: var(--muted);
  font-size: 16px;
  line-height: 1.9;
}

.section-kicker,
.preview-eyebrow,
.solution-index {
  color: var(--brand-strong);
}

.section-feature {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.9), rgba(234, 247, 255, 0.86));
}

.feature-showcase {
  display: grid;
  grid-template-columns: minmax(300px, 0.42fr) minmax(0, 0.58fr);
  gap: 24px;
  margin-top: 36px;
}

.feature-menu {
  display: grid;
  gap: 14px;
}

.feature-menu-tip {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 2px 4px 8px;
}

.feature-menu-tip p {
  margin: 0;
  color: rgba(16, 33, 77, 0.72);
  font-size: 13px;
  font-weight: 600;
  line-height: 1.6;
}

.feature-menu-dot {
  flex: 0 0 auto;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0f6fff, #39d3ff);
  box-shadow: 0 0 0 6px rgba(15, 111, 255, 0.08);
}

.feature-tab,
.solution-card,
.process-card,
.growth-point,
.contact-item {
  background: #ffffff;
  border: 1px solid var(--line);
}

.feature-tab {
  position: relative;
  overflow: hidden;
  display: block;
  width: 100%;
  padding: 22px;
  border-radius: 24px;
  text-align: left;
  cursor: pointer;
  box-shadow: 0 22px 42px rgba(20, 76, 159, 0.05);
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease, background 0.22s ease;
}

.feature-tab::before {
  content: '';
  position: absolute;
  inset: 16px auto 16px 0;
  width: 4px;
  border-radius: 999px;
  background: linear-gradient(180deg, #0f6fff, #39d3ff);
  opacity: 0;
  transition: opacity 0.22s ease;
}

.feature-tab:hover,
.feature-tab.active {
  transform: translateY(-2px);
  border-color: rgba(15, 111, 255, 0.24);
  background: linear-gradient(180deg, #ffffff 0%, #f5fbff 100%);
  box-shadow: 0 28px 48px rgba(15, 111, 255, 0.12);
}

.feature-tab:hover::before,
.feature-tab.active::before {
  opacity: 1;
}

.feature-tab-tag {
  display: inline-flex;
  padding: 6px 10px;
  border-radius: 999px;
  background: #eef7ff;
  color: var(--brand-strong);
  font-size: 12px;
  font-weight: 700;
}

.feature-tab strong {
  display: block;
  margin-top: 14px;
  font-size: 19px;
  line-height: 1.45;
  letter-spacing: 0.01em;
  color: var(--ink);
}

.feature-tab p,
.preview-points,
.solution-card ul {
  margin: 12px 0 0;
}

.feature-tab p,
.preview-text,
.solution-card li,
.process-card p,
.contact-item p {
  color: var(--muted);
}

.feature-preview {
  overflow: hidden;
  border-radius: 32px;
  background:
    radial-gradient(circle at top right, rgba(57, 211, 255, 0.22), transparent 28%),
    linear-gradient(135deg, #0c3f9b 0%, #0f71ff 46%, #45d8ff 100%);
  box-shadow: 0 34px 64px rgba(15, 111, 255, 0.18);
}

.preview-topbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 22px;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.92);
}

.preview-topbar p {
  margin: 0 0 0 4px;
  font-size: 13px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.78);
}

.preview-body {
  display: grid;
  grid-template-columns: minmax(0, 0.52fr) minmax(280px, 0.48fr);
  gap: 22px;
  padding: 26px;
  align-items: center;
}

.preview-copy {
  color: #ffffff;
}

.preview-eyebrow {
  color: rgba(255, 255, 255, 0.74);
}

.preview-copy h3 {
  margin-top: 16px;
  font-size: clamp(22px, 2.2vw, 32px);
  line-height: 1.36;
}

.preview-text {
  margin-top: 14px;
  color: rgba(255, 255, 255, 0.82);
}

.preview-points {
  padding-left: 18px;
  color: rgba(255, 255, 255, 0.88);
  line-height: 1.8;
}

.preview-panel {
  padding: 8px;
}

.panel-glass {
  padding: 20px;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.22);
  backdrop-filter: blur(18px);
}

.panel-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.panel-chip {
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
}

.panel-mock {
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr);
  gap: 14px;
  margin-top: 18px;
}

.mock-sidebar,
.mock-main {
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.9);
}

.mock-sidebar {
  display: grid;
  align-content: start;
  gap: 12px;
  padding: 16px 12px;
}

.mock-sidebar span {
  height: 12px;
  background: #d9ebff;
}

.mock-main {
  padding: 16px;
}

.mock-hero {
  height: 110px;
  border-radius: 18px;
  background: linear-gradient(135deg, #0f71ff, #43d4ff);
}

.mock-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 14px;
}

.mock-grid span {
  height: 56px;
  background: #e8f4ff;
}

.mock-footer {
  height: 12px;
  margin-top: 14px;
  border-radius: 999px;
  background: #dcecff;
}

.section-cases {
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0)),
    linear-gradient(180deg, #3b4360 0%, #293754 100%);
  color: #ffffff;
}

.section-heading-light h2,
.section-heading-light p {
  color: #ffffff;
}

.section-heading-light p:last-child {
  color: rgba(255, 255, 255, 0.72);
}

.case-grid,
.solution-grid,
.process-grid {
  display: grid;
  gap: 22px;
  margin-top: 36px;
}

.case-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.case-card {
  padding: 26px;
  border-radius: 30px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(18px);
}

.case-visual {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 260px;
  border-radius: 24px;
  background:
    radial-gradient(circle at top, rgba(255, 255, 255, 0.22), transparent 42%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.04));
}

.case-phone {
  width: 178px;
  padding: 12px;
  border-radius: 34px;
  background: linear-gradient(180deg, #ffffff, #f1f8ff);
  box-shadow: 0 24px 46px rgba(7, 17, 48, 0.22);
}

.case-phone-head {
  width: 42%;
  height: 10px;
  margin: 0 auto 12px;
  border-radius: 999px;
  background: #d8e8ff;
}

.case-phone-body {
  padding: 12px;
  border-radius: 24px;
  background: #f8fbff;
}

.case-phone-banner {
  height: 80px;
  border-radius: 18px;
  background: linear-gradient(135deg, #0c7cff, #29d0ff);
}

.case-phone-profile {
  height: 62px;
  margin-top: 12px;
  border-radius: 18px;
  background: #e8f4ff;
}

.case-phone-list {
  display: grid;
  gap: 10px;
  margin-top: 12px;
}

.case-phone-list span {
  height: 10px;
  border-radius: 999px;
  background: #dcecff;
}

.case-tag {
  margin-top: 22px;
  color: #86d4ff;
}

.case-card h3 {
  margin: 14px 0 0;
  font-size: 22px;
  line-height: 1.42;
  letter-spacing: 0.01em;
}

.case-desc {
  margin-top: 14px;
  color: rgba(255, 255, 255, 0.76);
}

.case-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 18px;
}

.case-stats span {
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.9);
  font-size: 13px;
}

.section-growth {
  background: linear-gradient(180deg, #ffffff 0%, #f7fbff 100%);
}

.growth-layout {
  display: grid;
  grid-template-columns: minmax(0, 0.56fr) minmax(0, 0.44fr);
  gap: 34px;
  align-items: center;
}

.growth-board {
  padding: 26px;
  border-radius: 34px;
  background: linear-gradient(180deg, #ffffff 0%, #eef7ff 100%);
  border: 1px solid var(--line);
  box-shadow: 0 30px 56px rgba(20, 76, 159, 0.08);
}

.board-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  font-size: 16px;
  font-weight: 700;
}

.board-snapshot {
  display: grid;
  grid-template-columns: 90px minmax(0, 1fr);
  gap: 18px;
  margin-top: 20px;
}

.board-sidebar {
  display: grid;
  align-content: start;
  gap: 12px;
  padding: 18px 14px;
  border-radius: 24px;
  background: #eff7ff;
}

.board-sidebar span {
  height: 12px;
  border-radius: 999px;
  background: #cfe5ff;
}

.board-main {
  display: grid;
  gap: 12px;
}

.member-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 12px;
  align-items: center;
  padding: 16px 18px;
  border-radius: 22px;
  background: #f7fbff;
  border: 1px solid rgba(15, 111, 255, 0.08);
}

.member-id {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 12px;
  align-items: center;
}

.member-id strong {
  font-size: 15px;
}

.member-id span {
  margin-top: 3px;
  color: var(--muted);
  font-size: 12px;
}

.member-status,
.member-data {
  padding: 8px 12px;
  background: #ebf5ff;
  color: var(--brand-strong);
  font-size: 12px;
  font-weight: 700;
}

.growth-points {
  display: grid;
  gap: 14px;
  margin-top: 28px;
}

.growth-point {
  padding: 22px;
  border-radius: 24px;
  box-shadow: 0 20px 40px rgba(15, 111, 255, 0.05);
}

.growth-point strong {
  display: block;
  font-size: 19px;
}

.growth-point p {
  margin-top: 10px;
  font-size: 15px;
}

.section-solutions,
.section-process {
  background: linear-gradient(180deg, #f6fbff 0%, #ffffff 100%);
}

.solution-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.solution-card,
.process-card {
  padding: 26px;
  border-radius: 26px;
  box-shadow: 0 22px 42px rgba(15, 111, 255, 0.05);
}

.solution-index,
.process-step {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 46px;
  padding: 8px 12px;
  border-radius: 999px;
  background: #ebf5ff;
}

.solution-card h3,
.process-card h3 {
  margin-top: 16px;
  font-size: 19px;
  line-height: 1.42;
  letter-spacing: 0.01em;
}

.solution-card p,
.process-card p {
  margin-top: 12px;
}

.solution-card ul {
  padding-left: 18px;
  color: var(--ink);
  line-height: 1.8;
}

.process-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.section-highlight {
  background: linear-gradient(180deg, #ffffff 0%, #eaf7ff 100%);
}

.highlight-card {
  display: grid;
  grid-template-columns: minmax(0, 0.58fr) minmax(280px, 0.42fr);
  gap: 28px;
  align-items: center;
  padding: 34px;
  border-radius: 34px;
  background:
    radial-gradient(circle at top right, rgba(57, 211, 255, 0.22), transparent 26%),
    linear-gradient(135deg, #0d4ee8 0%, #0f71ff 42%, #38d2ff 100%);
  color: #ffffff;
  box-shadow: 0 30px 60px rgba(15, 111, 255, 0.18);
}

.highlight-copy p {
  color: rgba(255, 255, 255, 0.82);
}

.highlight-metrics {
  display: grid;
  gap: 14px;
}

.highlight-stat {
  padding: 20px 22px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.16);
  backdrop-filter: blur(18px);
}

.highlight-stat strong {
  font-size: 24px;
}

.highlight-stat span {
  margin-top: 8px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.88);
}

.section-contact {
  background: linear-gradient(180deg, #f6fbff 0%, #ffffff 100%);
}

.contact-card {
  display: grid;
  grid-template-columns: minmax(0, 0.52fr) minmax(320px, 0.48fr);
  gap: 28px;
  padding: 34px;
  border-radius: 34px;
  background: #ffffff;
  border: 1px solid var(--line);
  box-shadow: 0 26px 48px rgba(15, 111, 255, 0.08);
}

.contact-panel {
  display: grid;
  gap: 14px;
}

.contact-item {
  padding: 20px 22px;
  border-radius: 24px;
}

.contact-item strong {
  font-size: 18px;
}

.contact-item p {
  margin-top: 8px;
  font-size: 15px;
}

.footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 28px 0 48px;
}

.footer-brand {
  font-size: 18px;
  font-weight: 700;
}

.footer-text {
  margin: 4px 0 0;
  color: rgba(16, 33, 77, 0.58);
  font-size: 14px;
}

.floating-tools {
  position: fixed;
  right: 22px;
  bottom: 22px;
  z-index: 40;
  display: grid;
  gap: 12px;
  justify-items: end;
}

.floating-button {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 118px;
  padding: 12px 16px;
  border: 1px solid rgba(15, 111, 255, 0.14);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.94);
  color: var(--ink);
  box-shadow: 0 18px 34px rgba(15, 111, 255, 0.12);
  backdrop-filter: blur(16px);
  cursor: pointer;
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
}

.floating-button:hover,
.floating-button:focus {
  transform: translateY(-2px);
  border-color: rgba(15, 111, 255, 0.26);
  box-shadow: 0 24px 42px rgba(15, 111, 255, 0.18);
}

.floating-button-top {
  min-width: 94px;
}

.floating-button-marketing {
  color: #ffffff;
  background: linear-gradient(135deg, #0f6fff 0%, #39d3ff 100%);
  border-color: rgba(15, 111, 255, 0.18);
}

.floating-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(15, 111, 255, 0.1);
  font-size: 14px;
  font-weight: 700;
}

.floating-icon-logo img {
  width: 17px;
  height: 17px;
  object-fit: contain;
}

.floating-button-marketing .floating-icon {
  background: rgba(255, 255, 255, 0.16);
}

.marketing-tool {
  position: relative;
}

.marketing-panel {
  position: absolute;
  right: calc(100% + 14px);
  bottom: 0;
  width: 360px;
  padding: 18px;
  border: 1px solid rgba(15, 111, 255, 0.14);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 30px 54px rgba(15, 111, 255, 0.16);
  backdrop-filter: blur(18px);
}

.marketing-panel-header strong,
.qr-card strong {
  display: block;
}

.marketing-panel-header strong {
  font-size: 18px;
  color: var(--ink);
}

.marketing-panel-header p,
.qr-card p {
  margin: 6px 0 0;
  color: rgba(16, 33, 77, 0.66);
  line-height: 1.7;
}

.marketing-qr-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-top: 16px;
}

.qr-card {
  padding: 14px;
  border-radius: 18px;
  background: linear-gradient(180deg, #f8fbff 0%, #eef7ff 100%);
  border: 1px solid rgba(15, 111, 255, 0.1);
}

.qr-card strong {
  margin-top: 12px;
  font-size: 15px;
  color: var(--ink);
}

.qr-card p {
  font-size: 12px;
}

.qr-code {
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1;
  width: 100%;
  padding: 10px;
  border-radius: 14px;
  background-color: #ffffff;
  box-shadow:
    inset 0 0 0 1px rgba(15, 111, 255, 0.1),
    0 18px 32px rgba(16, 33, 77, 0.08);
  overflow: hidden;
}

.qr-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 12px;
  background: #ffffff;
}

.tool-fade-enter-active,
.tool-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.tool-fade-enter,
.tool-fade-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

@keyframes float-chip {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

@media (max-width: 1120px) {
  .hero-content,
  .feature-showcase,
  .growth-layout,
  .highlight-card,
  .contact-card {
    grid-template-columns: 1fr;
  }

  .case-grid,
  .solution-grid,
  .process-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .hero-stage {
    min-height: 520px;
    margin-top: 14px;
  }

  .device-center {
    left: 50%;
    transform: translateX(-50%);
  }

  .device-left {
    left: 90px;
  }

  .device-right {
    right: 80px;
  }
}

@media (max-width: 860px) {
  .wrap {
    width: min(100% - 28px, 1180px);
  }

  .topbar {
    position: relative;
    top: auto;
    left: auto;
    width: min(100% - 28px, 1180px);
    margin: 0 auto;
    transform: none;
    flex-direction: column;
    align-items: flex-start;
    padding: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
    backdrop-filter: none;
    box-shadow: none;
  }

  .nav-group {
    justify-content: flex-start;
  }

  .hero-content {
    padding-top: 44px;
  }

  .hero-metrics,
  .case-grid,
  .solution-grid,
  .process-grid {
    grid-template-columns: 1fr;
  }

  .section-heading {
    max-width: none;
  }

  .preview-body,
  .board-snapshot,
  .contact-card {
    grid-template-columns: 1fr;
  }

  .feature-showcase {
    gap: 16px;
    margin-top: 28px;
  }

  .feature-menu {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    overflow: visible;
    padding: 0;
  }

  .feature-menu-tip {
    display: none;
  }

  .feature-tab {
    min-height: 102px;
    padding: 16px 16px 16px 18px;
    border-radius: 20px;
    box-shadow: none;
  }

  .feature-tab p {
    display: none;
  }

  .feature-tab strong {
    margin-top: 10px;
    font-size: 16px;
    line-height: 1.4;
    display: -webkit-box;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    word-break: break-word;
  }

  .feature-tab:hover {
    transform: none;
    box-shadow: none;
  }

  .feature-tab.active {
    transform: none;
    box-shadow: 0 14px 26px rgba(15, 111, 255, 0.1);
  }

  .feature-preview {
    border-radius: 26px;
  }

  .preview-topbar {
    padding: 14px 16px;
  }

  .preview-topbar p {
    font-size: 12px;
  }

  .preview-body {
    gap: 18px;
    padding: 18px;
  }

  .preview-copy h3 {
    font-size: 24px;
    line-height: 1.42;
  }

  .preview-text {
    font-size: 14px;
    line-height: 1.85;
  }

  .preview-points {
    margin-top: 10px;
    padding-left: 16px;
    font-size: 14px;
    line-height: 1.85;
  }

  .preview-panel {
    padding: 0;
  }

  .panel-glass {
    padding: 16px;
    border-radius: 22px;
  }

  .panel-chip {
    font-size: 11px;
  }

  .panel-mock {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .mock-sidebar {
    grid-auto-flow: column;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    padding: 12px;
  }

  .mock-main {
    padding: 14px;
  }

  .mock-hero {
    height: 96px;
  }

  .member-row {
    grid-template-columns: 1fr;
  }

  .member-status,
  .member-data {
    justify-self: start;
  }

  .hero-stage {
    min-height: 440px;
  }

  .device-left,
  .device-right {
    display: none;
  }

  .device-center {
    left: 50%;
    width: min(280px, 100%);
    transform: translateX(-50%);
  }

  .chip-right,
  .chip-bottom {
    display: none;
  }
}

@media (max-width: 640px) {
  .hero {
    padding-bottom: 56px;
  }

  .floating-tools {
    right: 14px;
    bottom: 14px;
    gap: 10px;
  }

  .marketing-tool {
    display: none;
  }

  .floating-button {
    min-width: 106px;
    padding: 11px 14px;
    border-radius: 16px;
  }

  .marketing-panel {
    right: 0;
    bottom: calc(100% + 12px);
    width: min(320px, calc(100vw - 28px));
  }

  .marketing-qr-grid {
    grid-template-columns: 1fr;
  }

  .topbar {
    width: min(100% - 28px, 1180px);
  }

  .hero-copy h1 {
    font-size: 30px;
  }

  .section-heading h2,
  .growth-copy h2,
  .highlight-copy h2,
  .contact-copy h2 {
    font-size: 26px;
    line-height: 1.38;
  }

  .section {
    padding: 72px 0;
  }

  .section-heading p,
  .growth-copy p,
  .contact-copy p {
    font-size: 15px;
    line-height: 1.85;
  }

  .feature-menu {
    gap: 8px;
  }

  .feature-tab {
    min-height: 94px;
    padding: 14px 14px 14px 16px;
    border-radius: 20px;
  }

  .feature-tab-tag {
    font-size: 11px;
  }

  .preview-topbar {
    display: none;
  }

  .feature-preview,
  .highlight-card,
  .contact-card,
  .growth-board {
    border-radius: 26px;
  }

  .preview-body,
  .highlight-card,
  .contact-card,
  .growth-board {
    padding: 18px;
  }

  .preview-copy h3 {
    font-size: 21px;
    line-height: 1.46;
  }

  .preview-text,
  .preview-points {
    font-size: 14px;
  }

  .hero-stage {
    min-height: 380px;
  }

  .floating-chip {
    left: 12px;
    right: auto;
    top: 20px;
  }

  .device-center {
    top: 56px;
  }

  .footer {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/OfficialSite.vue